package org.moxultimate.topwithmox.productcrud.add

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.crud.*
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.productcrud.main.ProductCRUDState
import org.moxultimate.topwithmox.productcrud.main.ProductCRUDViewModel
import org.moxultimate.topwithmox.productcrud.main.ProductContentFormEntry
import org.moxultimate.topwithmox.productcrud.main.ProductFormEntry
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.utils.autoTagSync

/**
 * ProductAddPage
 * Two-phase page for creating a new product content entry and its child products.
 *
 * Phase 1: content form (title, type, tags, video, description) → "Create Content"
 * Phase 2: appears after the content is saved — inline product items section where
 *          the user can add as many products as needed before navigating away.
 */
@Composable
fun ProductAddPage() {
    val viewModel: ProductCRUDViewModel = koinViewModel()
    val crudState by viewModel.crudState.collectAsState()
    val productOpState by viewModel.productOpState.collectAsState()
    val createdContentId by viewModel.createdContentId.collectAsState()
    val navigator = rememberAppNavigator()

    var entry by remember { mutableStateOf(ProductContentFormEntry()) }
    var submitAttempted by remember { mutableStateOf(false) }

    // Pending new product forms (Phase 2)
    val pendingProducts = remember { mutableStateListOf<ProductFormEntry>() }
    var savingIndex by remember { mutableStateOf<Int?>(null) }

    // On product item saved: remove that form and reset state
    LaunchedEffect(productOpState) {
        if (productOpState is ProductCRUDState.Success) {
            savingIndex?.let { pendingProducts.removeAt(it) }
            savingIndex = null
            viewModel.resetProductState()
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(Colors.White)
            .overflow(Overflow.Auto),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .width(clamp(min = 700.px, value = 70.vw, max = 1100.px))
                .padding(top = 80.px, bottom = 48.px, leftRight = 2.cssRem),
            verticalArrangement = Arrangement.spacedBy(28.px),
        ) {
            // Page title
            SpanText(
                text = "Add Product Content",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(32.px)
                    .fontWeight(FontWeight.Bold)
                    .moxTextGradient(),
            )

            // ── Phase 1: Content form ─────────────────────────────────────────
            if (createdContentId == null) {
                // Error banner
                if (crudState is ProductCRUDState.Error) {
                    CRUDStatusBanner(
                        message = (crudState as ProductCRUDState.Error).message,
                        isError = true,
                        onDismiss = { viewModel.resetState() },
                    )
                }

                // Validation hint after submit attempt
                if (submitAttempted && !entry.isValid) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(leftRight = 12.px, topBottom = 8.px)
                            .borderRadius(8.px)
                            .backgroundColor(Colors.MistyRose)
                            .border(1.px, LineStyle.Solid, Colors.LightCoral),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SpanText(
                            text = "Missing required: ${entry.missingFields.joinToString(" · ")}",
                            modifier = Modifier
                                .fontFamily(FontFamily)
                                .fontSize(12.px)
                                .color(Colors.Crimson),
                        )
                    }
                }

                CRUDFormField(
                    label = "Title *",
                    value = entry.title,
                    placeholder = "e.g. Best Gaming Mouse 2026",
                    onValueChange = { entry = entry.copy(title = it, tags = autoTagSync(entry.tags, entry.title, it)) },
                )

                CRUDDropdown(
                    label = "Type",
                    options = crudProductContentTypes,
                    selected = entry.type,
                    placeholder = "Select type…",
                    onSelected = { entry = entry.copy(type = it, tags = autoTagSync(entry.tags, entry.type, it)) },
                )

                CRUDExistingDropZone(
                    label = "Thumbnail",
                    existingUrl = entry.thumbnailPath,
                    file = entry.thumbnailFile,
                    onFileDropped = { entry = entry.copy(thumbnailFile = it) },
                    onFileRemoved = { entry = entry.copy(thumbnailFile = null) },
                )

                CRUDFormField(
                    label = "Tags",
                    value = entry.tags,
                    placeholder = "e.g. Gaming | Mouse | Budget (pipe-separated)",
                    onValueChange = { entry = entry.copy(tags = it) },
                )

                CRUDFormField(
                    label = "Video Link",
                    value = entry.videoLink,
                    placeholder = "YouTube URL (optional)",
                    onValueChange = { entry = entry.copy(videoLink = it) },
                )

                CRUDFormField(
                    label = "Description",
                    value = entry.description,
                    placeholder = "Short paragraph shown below the section title",
                    onValueChange = { entry = entry.copy(description = it) },
                )

                val isLoading = crudState is ProductCRUDState.Loading
                GradientButton(
                    text = if (isLoading) "Creating…" else "Create Content",
                    disabled = isLoading,
                    onClick = {
                        submitAttempted = true
                        if (entry.isValid && !isLoading) viewModel.createContent(entry)
                    },
                )
            }

            // ── Phase 2: Product items section (shown after content is created) ─
            if (createdContentId != null) {
                val contentId = createdContentId!!

                // Success confirmation banner
                CRUDStatusBanner(
                    message = "Content \"${entry.title}\" created! Now add your products below.",
                    isError = false,
                    onDismiss = {},
                )

                // Product operation error banner
                if (productOpState is ProductCRUDState.Error) {
                    CRUDStatusBanner(
                        message = (productOpState as ProductCRUDState.Error).message,
                        isError = true,
                        onDismiss = { viewModel.resetProductState() },
                    )
                }

                // Section header
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        text = "Products",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(18.px)
                            .fontWeight(FontWeight.Bold)
                            .moxTextGradient(),
                    )
                    GradientButton(
                        text = "+ Add Product",
                        buttonHeight = 40,
                        onClick = { pendingProducts.add(ProductFormEntry()) },
                    )
                }

                if (pendingProducts.isEmpty()) {
                    SpanText(
                        text = "Click \"+ Add Product\" to add products to this content.",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(14.px)
                            .color(Colors.Gray),
                    )
                }

                pendingProducts.forEachIndexed { index, productEntry ->
                    val isSaving = productOpState is ProductCRUDState.Loading && savingIndex == index

                    // Divider between product forms
                    if (index > 0) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(1.px)
                                .backgroundColor(Colors.LightGray)
                                .margin(topBottom = 4.px),
                        )
                    }

                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(20.px),
                    ) {
                        // Product form header with rank and remove button
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(10.px),
                            ) {
                                Box(
                                    modifier = Modifier
                                        .size(28.px)
                                        .borderRadius(14.px)
                                        .styleModifier { property("background", "linear-gradient(135deg, #f47b20, #e84393)") },
                                    contentAlignment = Alignment.Center,
                                ) {
                                    SpanText(
                                        text = "${index + 1}",
                                        modifier = Modifier
                                            .fontFamily(FontFamily)
                                            .fontSize(12.px)
                                            .fontWeight(FontWeight.Bold)
                                            .color(Colors.White),
                                    )
                                }
                                SpanText(
                                    text = "New Product",
                                    modifier = Modifier
                                        .fontFamily(FontFamily)
                                        .fontSize(15.px)
                                        .fontWeight(FontWeight.Bold)
                                        .color(DarkTextColor),
                                )
                            }
                            SpanText(
                                text = "✕ Remove",
                                modifier = Modifier
                                    .fontFamily(FontFamily)
                                    .fontSize(13.px)
                                    .color(Colors.Gray)
                                    .styleModifier { property("cursor", "pointer") }
                                    .onClick { pendingProducts.removeAt(index) },
                            )
                        }

                        ProductFormFields(
                            entry = productEntry,
                            onEntryChange = { pendingProducts[index] = it },
                        )

                        GradientButton(
                            text = if (isSaving) "Saving…" else "Save Product",
                            disabled = !productEntry.isValid || productOpState is ProductCRUDState.Loading,
                            onClick = {
                                if (productEntry.isValid && productOpState !is ProductCRUDState.Loading) {
                                    savingIndex = index
                                    viewModel.createProductItem(contentId, productEntry)
                                }
                            },
                        )
                    }
                }

                // Done button — navigates back to CRUD list
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(1.px)
                        .backgroundColor(Colors.LightGray)
                        .margin(topBottom = 8.px),
                )
                GradientButton(
                    text = "Done → Go to Products",
                    onClick = {
                        viewModel.resetCreatedContentId()
                        navigator.goToProductCRUD()
                    },
                )
            }
        }
    }
}

/**
 * ProductFormFields
 * Reusable form layout for all product fields, organized in labelled groups.
 * Used by both [ProductItemAddPage] and [org.moxultimate.topwithmox.productcrud.update.ProductItemUpdatePage].
 *
 * @param entry Current form state.
 * @param onEntryChange Emits an updated copy whenever any field changes.
 */
@Composable
fun ProductFormFields(
    entry: ProductFormEntry,
    onEntryChange: (ProductFormEntry) -> Unit,
) {
    // ── Basic Info ────────────────────────────────────────────────────────────
    FormSection(title = "Basic Info") {
        CRUDExistingDropZone(
            label = "Product Image",
            existingUrl = entry.imagePath,
            file = entry.imageFile,
            onFileDropped = { onEntryChange(entry.copy(imageFile = it)) },
            onFileRemoved = { onEntryChange(entry.copy(imageFile = null)) },
        )
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.px)) {
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Brand *",
                    value = entry.brand,
                    placeholder = "e.g. Logitech",
                    onValueChange = { onEntryChange(entry.copy(brand = it)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Name *",
                    value = entry.name,
                    placeholder = "e.g. G Pro X Superlight 2",
                    onValueChange = { onEntryChange(entry.copy(name = it)) },
                )
            }
        }
        CRUDDropdown(
            label = "Label *",
            options = crudProductLabels,
            selected = entry.label,
            placeholder = "Select label…",
            onSelected = { onEntryChange(entry.copy(label = it)) },
        )
        CRUDFormField(
            label = "Description *",
            value = entry.description,
            placeholder = "Short product description",
            onValueChange = { onEntryChange(entry.copy(description = it)) },
        )
    }

    // ── Pros & Cons ───────────────────────────────────────────────────────────
    FormSection(title = "Pros & Cons") {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.px)) {
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Pros",
                    value = entry.pros,
                    placeholder = "Great battery | Lightweight | Precise sensor",
                    onValueChange = { onEntryChange(entry.copy(pros = it)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Cons",
                    value = entry.cons,
                    placeholder = "Expensive | No RGB | Plastic feet",
                    onValueChange = { onEntryChange(entry.copy(cons = it)) },
                )
            }
        }
    }

    // ── Shopee ────────────────────────────────────────────────────────────────
    FormSection(title = "Shopee") {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.px)) {
            Box(modifier = Modifier.weight(2f)) {
                CRUDFormField(
                    label = "Shopee Link",
                    value = entry.shopeeLink,
                    placeholder = "https://shopee.ph/...",
                    onValueChange = { onEntryChange(entry.copy(shopeeLink = it)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Price",
                    value = entry.shopeePrice,
                    placeholder = "3,999",
                    onValueChange = { onEntryChange(entry.copy(shopeePrice = it)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Promo",
                    value = entry.shopeePromo,
                    placeholder = "Up to 20% off",
                    onValueChange = { onEntryChange(entry.copy(shopeePromo = it)) },
                )
            }
        }
    }

    // ── Lazada ────────────────────────────────────────────────────────────────
    FormSection(title = "Lazada") {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.px)) {
            Box(modifier = Modifier.weight(2f)) {
                CRUDFormField(
                    label = "Lazada Link",
                    value = entry.lazadaLink,
                    placeholder = "https://lazada.com.ph/...",
                    onValueChange = { onEntryChange(entry.copy(lazadaLink = it)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Price",
                    value = entry.lazadaPrice,
                    placeholder = "3,899",
                    onValueChange = { onEntryChange(entry.copy(lazadaPrice = it)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Promo",
                    value = entry.lazadaPromo,
                    placeholder = "Free shipping",
                    onValueChange = { onEntryChange(entry.copy(lazadaPromo = it)) },
                )
            }
        }
    }

    // ── Amazon ────────────────────────────────────────────────────────────────
    FormSection(title = "Amazon") {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.px)) {
            Box(modifier = Modifier.weight(2f)) {
                CRUDFormField(
                    label = "Amazon Link",
                    value = entry.amazonLink,
                    placeholder = "https://amazon.com/...",
                    onValueChange = { onEntryChange(entry.copy(amazonLink = it)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Price",
                    value = entry.amazonPrice,
                    placeholder = "79.99",
                    onValueChange = { onEntryChange(entry.copy(amazonPrice = it)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Promo",
                    value = entry.amazonPromo,
                    placeholder = "Save $10 coupon",
                    onValueChange = { onEntryChange(entry.copy(amazonPromo = it)) },
                )
            }
        }
    }

    // ── TikTok ────────────────────────────────────────────────────────────────
    FormSection(title = "TikTok Shop") {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.px)) {
            Box(modifier = Modifier.weight(2f)) {
                CRUDFormField(
                    label = "TikTok Link",
                    value = entry.tiktokLink,
                    placeholder = "https://tiktok.com/shop/...",
                    onValueChange = { onEntryChange(entry.copy(tiktokLink = it)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Price",
                    value = entry.tiktokPrice,
                    placeholder = "3,799",
                    onValueChange = { onEntryChange(entry.copy(tiktokPrice = it)) },
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                CRUDFormField(
                    label = "Promo",
                    value = entry.tiktokPromo,
                    placeholder = "Flash sale",
                    onValueChange = { onEntryChange(entry.copy(tiktokPromo = it)) },
                )
            }
        }
    }
}

/**
 * FormSection
 * Labeled group container used to separate product form fields by category.
 *
 * @param title Section heading label.
 * @param content Fields to render inside the section.
 */
@Composable
internal fun FormSection(title: String, content: @Composable () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.px, LineStyle.Solid, Colors.LightGray)
            .borderRadius(12.px)
            .padding(all = 20.px),
        verticalArrangement = Arrangement.spacedBy(16.px),
    ) {
        SpanText(
            text = title,
            modifier = Modifier
                .fontFamily(FontFamily)
                .fontSize(13.px)
                .fontWeight(FontWeight.Bold)
                .moxTextGradient()
                .styleModifier { property("text-transform", "uppercase") }
                .letterSpacing(0.8.px),
        )
        content()
    }
}
